import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { StiiraError } from '@core/models';
import { NewTemplatePost } from '@core/models/case-communication/new-template-post.model';
import { ErrorService, LayoutService } from '@core/services';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Observable, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AddNewTemplateDialog, AddNewTemplateForm } from '@core/models/case-communication/add-new-template-dialog.model';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';

@Component({
    selector: 'app-add-new-email-template',
    templateUrl: './add-new-email-template.component.html',
    styleUrls: ['./add-new-email-template.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddNewEmailTemplateComponent implements OnInit {
  static readonly componentName = 'AddNewEmailTemplateComponent';
  
  public form: FormGroup<AddNewTemplateForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;

  public position = position;
  public onDragEnded = onDragEnded;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      addNewTemplateDialog: AddNewTemplateDialog,
      sourceId: number,
      sourceName: string,
      sourceType: string,
      sysText: any,
      unsavedChangesSysText: any
    },
    private dialogRef: MatDialogRef<AddNewEmailTemplateComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private service: CaseCommunicationService,
    private errorService: ErrorService,
    private layoutService: LayoutService
  ) {
    this.isSubmitting$ = new Subject();

    this.form = this.fb.group({
      templateName: this.fb.control(null, Validators.required),
      emailType: this.fb.control(null, Validators.required),
      sourceType: this.fb.control(null, Validators.required)
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    this.form.patchValue({
      templateName: (this.data.sourceName
        ? this.data.sourceName + ' (' + this.data.sysText.copy + ')'
        : null),
      emailType: null,
      sourceType: this.data.sourceType
    });

    this.formInitValues = { ...this.form.value };
  }

  onSubmit() {
    let newTemplatePost: NewTemplatePost = {
      templateName: this.form.controls.templateName.value,
      emailType: (this.data.sourceId == null
        ? this.form.controls.emailType.value
        : null),
      sourceId: this.data.sourceId
    };

    this.service
      .postNewTemplate(newTemplatePost)
      .pipe(
        indicate(this.isSubmitting$),
        finalize(() => { })
      )
      .subscribe(
        (res) => { this.dialogRef.close(res); },
        (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
      );
  }

  public cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    }
    else {
      this.openUnsavedChangesDialog();
    }
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }
}
